import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "components/Layout";

const HomePage = () => {
  useEffect(() => {
    const items = document.querySelectorAll(".fade-in");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.16 }
    );
    items.forEach((it) => observer.observe(it));
    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      {/* ─── 1. Hero ─── */}
      <section className="hero">
        <div className="hero-inner">
          <div>
            <div className="hero-tag">
              <span className="hero-tag-dot" />
              Mai-Aktion 2026 · Hamburg
            </div>
            <h1>
              Das <em>Dr. Hoffmann</em>
              <br />
              -Prinzip
            </h1>
            <div className="hero-sub">
              <span>Schnell</span>
              <span>Smart</span>
              <span>Skalierbar</span>
            </div>
            <p className="hero-lead">
              Effizienz trifft Wellness. Ein neues, schnelles Massagekonzept für
              Studios, Unternehmer und mobile Anbieter — entwickelt von
              Dr. Hoffmann mit Master Sura. Start in Hamburg, Ausbau PLZ-basiert.
            </p>
            <div className="hero-actions">
              <Link to="/buchen" className="btn-primary">
                Kostenlosen Erstbesuch buchen
              </Link>
              <a href="#lizenz" className="btn-ghost">
                Gebietsschutz sichern
              </a>
            </div>
          </div>

          <div className="hero-visual fade-in">
            <div>
              <div className="hero-visual-label">Das Konzept in Zahlen</div>
              <div className="hero-visual-headline">
                <strong>10-Minuten</strong> Perkussiv-Massage.
                <br />
                Sofort buchbar &amp; sofort vermarktbar.
              </div>
            </div>
            <div className="hero-visual-stats">
              <div className="hero-visual-stat">
                <div className="value">+63 %</div>
                <div className="label">höherer Stundenumsatz</div>
              </div>
              <div className="hero-visual-stat">
                <div className="value">6 / h</div>
                <div className="label">Behandlungen / Stunde</div>
              </div>
              <div className="hero-visual-stat">
                <div className="value">29 €</div>
                <div className="label">Studio-Tarif / 10 Min.</div>
              </div>
              <div className="hero-visual-stat">
                <div className="value">EQ-150</div>
                <div className="label">Flüsterleiser Motor</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Trust strip ─── */}
      <div className="trust-strip">
        <span><strong>4-in-1</strong> Massagekonzept</span>
        <span><strong>Behandlung in Alltagskleidung</strong> möglich</span>
        <span><strong>Flüsterleiser EQ-150 Motor</strong></span>
        <span><strong>Lebenslange Lizenz</strong></span>
      </div>

      {/* ─── 2. Idea ─── */}
      <section className="section" id="konzept">
        <div className="fade-in">
          <span className="section-kicker">Die Idee</span>
          <h2 className="section-title">
            Wellness, Effizienz und <em>moderne Vertriebsstrukturen</em> — in einem System.
          </h2>
        </div>
        <div className="two-col">
          <p className="section-lead fade-in">
            Das Dr. Hoffmann-Prinzip verbindet kurze, intensive Massage-Sessions
            mit einem skalierbaren Lizenz- und Hybrid-Vertriebsmodell. Mehr
            Buchungen, mehr Sichtbarkeit und ein höherer Stundenumsatz für
            Studios, Unternehmer und mobile Anbieter.
          </p>
          <ul className="two-col-list fade-in">
            <li>Mehr Buchungen &amp; Sichtbarkeit</li>
            <li>Höherer Umsatz pro Stunde</li>
            <li>Hybrider Vertriebsweg</li>
            <li>Schnell buchbar</li>
            <li>Einfach skalierbar</li>
            <li>Sofort vermarktbar</li>
          </ul>
        </div>
      </section>

      {/* ─── 3. Why Dr. Hoffmann-Massage ─── */}
      <section className="section section-alt">
        <div className="fade-in">
          <span className="section-kicker">Warum Dr. Hoffmann?</span>
          <h2 className="section-title">
            Fünf Gründe, die das <em>Prinzip einzigartig</em> machen.
          </h2>
        </div>
        <div className="card-grid">
          {[
            { icon: "⚡", title: "Schnell", body: "Nur ca. 10 Minuten pro Session — maximale Wirkung in minimaler Zeit." },
            { icon: "◎", title: "Effektiv", body: "4-in-1 Massagekonzept, das tief liegende Muskelpartien gezielt anspricht." },
            { icon: "↻", title: "Einfach", body: "Behandlung in Alltagskleidung möglich — kein Umkleiden, kein Aufwand." },
            { icon: "♪", title: "Flüsterleise", body: "EQ-150 Motor für diskreten Einsatz in Studios, Büros und bei Events." },
            { icon: "✦", title: "Flexibel", body: "Überall einsetzbar — im Studio, Büro, bei Events oder Gesundheitstagen." },
          ].map((c) => (
            <div className="card fade-in" key={c.title}>
              <div className="card-icon">{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 4. License model ─── */}
      <section className="section" id="lizenz">
        <div className="two-col" style={{ alignItems: "start" }}>
          <div className="fade-in">
            <span className="section-kicker">Das Lizenzmodell</span>
            <h2 className="section-title">
              MediConsult Group liefert Ihnen das <em>komplette System</em>.
            </h2>
            <p className="section-lead">
              Sie konzentrieren sich auf Ihre Kunden — wir stellen Ihnen die
              Infrastruktur, das Marketing und den Vertrieb zur Verfügung, damit
              Sie das Angebot schnell und professionell starten können.
            </p>
            <div style={{ marginTop: "1.8rem" }}>
              <Link to="/buchen" className="btn-primary">
                Kostenlosen Erstbesuch buchen
              </Link>
            </div>
          </div>
          <div className="license-panel fade-in">
            <h3>MediConsult Group übernimmt:</h3>
            <ul>
              <li>Onboarding</li>
              <li>Hybrid-Marketing &amp; Kampagnen</li>
              <li>Buchung &amp; Terminverwaltung</li>
              <li>CRM-System</li>
              <li>Gebietsentwicklung nach Postleitzahl</li>
              <li>Vertriebsunterstützung</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ─── 5. Target groups ─── */}
      <section className="section section-alt">
        <div className="fade-in">
          <span className="section-kicker">Zielgruppen</span>
          <h2 className="section-title">
            Für wen ist das <em>Angebot gemacht</em>?
          </h2>
        </div>
        <div className="card-grid">
          {[
            {
              icon: "✦",
              title: "Wellness & Kosmetik",
              body: "Optimale Ergänzung für bestehende Studios — neue Kunden, höhere Frequenz.",
            },
            {
              icon: "◇",
              title: "Fitness & Sport",
              body: "Regeneration für Mitglieder als Zusatz-Service mit klarer Marge.",
            },
            {
              icon: "◎",
              title: "Unternehmer",
              body: "Ideal für Top-Locations, mobile Anbieter, Event- und Gesundheitstage.",
            },
          ].map((c) => (
            <div className="card fade-in" key={c.title}>
              <div className="card-icon">{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
        <p
          className="section-lead fade-in"
          style={{ textAlign: "center", margin: "2.2rem auto 0", fontWeight: 600, color: "var(--dark-blue)" }}
        >
          Sichern Sie sich jetzt Ihren <strong>Gebietsschutz per Postleitzahl</strong>.
        </p>
      </section>

      {/* ─── 6. Pricing ─── */}
      <section className="section" id="tarife">
        <div className="fade-in">
          <span className="section-kicker">Unsere Tarife</span>
          <h2 className="section-title">
            Klare Preise — <em>direkt vermarktbar</em>.
          </h2>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card fade-in is-featured">
            <h3>Studio-Tarif</h3>
            <div className="pricing-price">
              29 € <small>/ 10 Min.</small>
            </div>
            <p>Ideal für Laufkundschaft und schnellen Durchlauf im Studio oder Center.</p>
            <div style={{ marginTop: "1.4rem" }}>
              <Link to="/buchen" className="btn-primary">Termin im Studio</Link>
            </div>
          </div>
          <div className="pricing-card fade-in">
            <h3>Mobil-Tarif</h3>
            <div className="pricing-price">
              39 € <small>/ 10 Min.</small>
            </div>
            <p>Perfekt für Büros, Events, Gesundheitstage und mobile Anbieter mit Premium-Anspruch.</p>
            <div style={{ marginTop: "1.4rem" }}>
              <Link to="/buchen" className="btn-ghost">Mobile Buchung</Link>
            </div>
          </div>
        </div>
        <p
          className="section-lead fade-in"
          style={{ textAlign: "center", margin: "2.2rem auto 0", fontStyle: "italic" }}
        >
          Schnell buchbar. Einfach skalierbar. Sofort vermarktbar.
        </p>
      </section>

      {/* ─── 7. Market comparison ─── */}
      <section className="section section-alt">
        <div className="fade-in">
          <span className="section-kicker">Marktvergleich</span>
          <h2 className="section-title">
            Verdienstmöglichkeiten im <em>direkten Vergleich</em>.
          </h2>
        </div>
        <div className="compare-grid">
          <div className="compare-card is-classic fade-in">
            <h3>Klassischer Massage-Markt</h3>
            <div className="compare-stat"><span>Dauer</span><strong>60 Min.</strong></div>
            <div className="compare-stat"><span>Umsatz / Sitzung</span><strong>ca. 70 €</strong></div>
            <div className="compare-stat"><span>Umsatz / Stunde</span><strong>ca. 70 €</strong></div>
          </div>
          <div className="compare-card is-highlight fade-in">
            <h3>Dr. Hoffmann-Massage</h3>
            <div className="compare-stat"><span>Dauer</span><strong>10 Min.</strong></div>
            <div className="compare-stat"><span>Behandlungen / Stunde</span><strong>6</strong></div>
            <div className="compare-stat"><span>Umsatz / Sitzung (UPE)</span><strong>19 €</strong></div>
            <div className="compare-stat"><span>Umsatz / Stunde</span><strong>114 €</strong></div>
          </div>
        </div>
        <p className="compare-callout fade-in">
          Das Dr. Hoffmann-Prinzip ermöglicht bei deutlich kürzerer Behandlungsdauer{" "}
          <strong>+63 % höheren Stundenumsatz</strong>{" "}
          als klassische Massageangebote.
        </p>
        <p
          className="revenue-note fade-in"
          style={{ textAlign: "center", marginTop: "1.4rem" }}
        >
          Rechenweg: 29 € Studio-Tarif − 10 € Dienstleistungspauschale = 19 € pro Sitzung · 6 × 19 € = 114 € / Stunde.
        </p>
      </section>

      {/* ─── 8. Revenue model ─── */}
      <section className="section">
        <div className="fade-in">
          <span className="section-kicker">Umsatzmodell</span>
          <h2 className="section-title">
            Beispielrechnung für <em>Lizenznehmer</em>.
          </h2>
          <p className="section-lead">
            Basis: 6 Massagen pro Stunde nach UPE. Beispielrechnung vor Steuern und Fixkosten.
          </p>
        </div>
        <div className="revenue-grid">
          <div className="revenue-card fade-in">
            <span className="revenue-tag">Vor Ort</span>
            <h3>Massageservice im Studio</h3>
            <div className="revenue-stats">
              <div className="revenue-stat-row"><span>Umsatz pro Stunde</span><strong>114 €</strong></div>
              <div className="revenue-stat-row"><span>Umsatz pro Monat</span><strong>9.120 €</strong></div>
            </div>
            <p>Stationäre Präsenz im Center · idealer Kundenfluss &amp; hohe Sichtbarkeit.</p>
          </div>
          <div className="revenue-card fade-in">
            <span className="revenue-tag">Mobil</span>
            <h3>Flexibler Außeneinsatz</h3>
            <div className="revenue-stats">
              <div className="revenue-stat-row"><span>Umsatz pro Stunde</span><strong>174 €</strong></div>
              <div className="revenue-stat-row"><span>Umsatz pro Monat</span><strong>13.920 €</strong></div>
            </div>
            <p>Höhere Flexibilität &amp; Reichweite · attraktives Premium-Preissegment.</p>
          </div>
        </div>
        <p className="revenue-note fade-in" style={{ textAlign: "center" }}>
          Beispielrechnung vor Steuern und Fixkosten · keine Garantie auf bestimmte Einnahmen.
        </p>
      </section>

      {/* ─── 9. Starter package ─── */}
      <section className="section">
        <div className="starter fade-in">
          <div>
            <span className="starter-tag">Mai-Aktion 2026</span>
            <h3>Unser Starterpaket — alles, was Sie zum Start brauchen.</h3>
            <ul>
              <li>Perkussiv-Massagegerät</li>
              <li>1× Ausbildung</li>
              <li>1× Zertifikat</li>
              <li>Zugang CRM &amp; Buchungssystem</li>
              <li>Einbindung Hybrid-Vertrieb</li>
              <li>Lebenslange Dr. Hoffmann-Prinzip Lizenz</li>
            </ul>
          </div>
          <div className="starter-price">
            <div className="label">Einmalig</div>
            <div className="amount">900 €</div>
            <div className="sub">Komplett-Setup inkl. Lizenz</div>
            <Link to="/buchen" className="btn-primary">Erstgespräch sichern</Link>
          </div>
        </div>
      </section>

      {/* ─── 10. Expansion ─── */}
      <section className="section section-alt">
        <div className="fade-in">
          <span className="section-kicker">Der Vertriebsweg</span>
          <h2 className="section-title">
            Das <em>Schneckenhaus-Prinzip</em>.
          </h2>
          <p className="section-lead">
            Wir starten im Herzen Hamburgs, Cremon 11, und dehnen uns PLZ-basiert
            über das gesamte Stadtgebiet und später bundesweit aus. Sichern Sie
            sich jetzt Ihren Gebietsschutz für Ihre Postleitzahl, bevor diese
            vergeben ist.
          </p>
        </div>
        <div className="expansion">
          <div className="fade-in">
            <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", marginBottom: "1rem" }}>
              So expandieren wir — PLZ für PLZ
            </h3>
            <ul className="two-col-list" style={{ gridTemplateColumns: "1fr" }}>
              <li>Schritt 1 — Hamburg Altstadt (20457)</li>
              <li>Schritt 2 — Erweiterung im 20xxx- und 22xxx-Gebiet</li>
              <li>Schritt 3 — Großraum Hamburg 21xxx + Umland</li>
              <li>Schritt 4 — Bundesweiter Ausbau über Master-Lizenzpartner</li>
            </ul>
            <div style={{ marginTop: "1.6rem" }}>
              <Link to="/buchen" className="btn-primary">PLZ-Gebiet anfragen</Link>
            </div>
          </div>
          <div className="expansion-visual fade-in" aria-hidden="true">
            <svg viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="ring" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#1c2a8e" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#1c2a8e" stopOpacity="0" />
                </radialGradient>
              </defs>
              <circle cx="160" cy="160" r="150" fill="url(#ring)" />
              <circle cx="160" cy="160" r="120" fill="none" stroke="#1c2a8e" strokeOpacity="0.18" strokeWidth="1" />
              <circle cx="160" cy="160" r="90" fill="none" stroke="#1c2a8e" strokeOpacity="0.28" strokeWidth="1" />
              <circle cx="160" cy="160" r="60" fill="none" stroke="#1c2a8e" strokeOpacity="0.38" strokeWidth="1" />
              <circle cx="160" cy="160" r="30" fill="none" stroke="#1c2a8e" strokeOpacity="0.55" strokeWidth="1.5" />
              <path
                d="M 160,160 m 0,-115 a 115,115 0 0,1 90,180 a 90,90 0 0,1 -150,-30 a 60,60 0 0,1 90,-30"
                fill="none"
                stroke="#0042b6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="6 4"
              />
              <text x="160" y="158" textAnchor="middle" fill="#1c2a8e" fontFamily="Manrope, sans-serif" fontWeight="700" fontSize="12">
                Hamburg
              </text>
              <text x="160" y="174" textAnchor="middle" fill="#0042b6" fontFamily="Inter, sans-serif" fontSize="10">
                20457 · Cremon 11
              </text>
            </svg>
            <div className="expansion-pin" />
          </div>
        </div>
      </section>

      {/* ─── 11. Mai-Aktion ─── */}
      <section className="section" id="aktion">
        <div className="fade-in">
          <span className="section-kicker">Exklusive Mai-Aktion</span>
          <h2 className="section-title">
            Jetzt Mai-Termin <em>sichern</em>.
          </h2>
        </div>
        <div className="campaign fade-in">
          <div className="campaign-grid">
            <div className="campaign-item">
              <h4>Gratis Besuch</h4>
              <p>Kostenlose Vorführung direkt bei Ihnen im Studio.</p>
            </div>
            <div className="campaign-item">
              <h4>Max. 6 Test-Massagen</h4>
              <p>Live-Demonstration durch Master Sura.</p>
            </div>
            <div className="campaign-item">
              <h4>Location-Check</h4>
              <p>Einschätzung des Standorts und PLZ-Gebiets.</p>
            </div>
          </div>
          <Link to="/buchen" className="btn-primary">
            Mai-Termin sichern
          </Link>
        </div>
      </section>

      {/* ─── 12. Final CTA ─── */}
      <section className="final-cta">
        <h2>Bereit für den Start?</h2>
        <p>Buchen Sie jetzt Ihren kostenlosen Erstbesuch und sichern Sie sich Ihren Gebietsschutz, bevor die PLZ vergeben ist.</p>
        <div className="final-cta-buttons">
          <Link to="/buchen" className="btn-primary">Kostenlosen Erstbesuch buchen</Link>
          <a href="https://mediconsult.group" target="_blank" rel="noopener noreferrer" className="btn-outline">
            mediconsult.group
          </a>
        </div>
        <div className="final-cta-contact">
          MediConsult Group · Cremon 11 · 20457 Hamburg
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
